/**
 * @fileoverview Syndicator Showdown — Quick Pick Auto-Fill Bar
 * @module components/QuickPickBar
 *
 * Compact inline bar rendered above the race card stack. Lets a player
 * replace their entire entry in one click using a chosen strategy —
 * Random, Market Fancies, or AI Value (falls back to "Budget Roughies"
 * when the slate has no value-engine output yet).
 *
 * Generation + validation is delegated entirely to utils/quickPick.js —
 * this component only wires the button UI, the replace-confirmation, and
 * applies the result via EntryContext.applyGeneratedStable().
 */

import React, { useMemo, useState, useCallback } from 'react';
import { useEntryState } from '../context/EntryContext';
import { runQuickPick, QUICK_PICK_STRATEGIES, hasValueEngineData } from '../utils/quickPick';
import './QuickPickBar.css';

const CONFIRM_MESSAGE = 'This will replace your current selections across all races. Continue?';

export const QuickPickBar = ({ races, avgSpendPerRace, kMultiplier }) => {
  const { perRaceFloor, totalStartingCapital, applyGeneratedStable } = useEntryState();
  const [busyStrategy, setBusyStrategy] = useState(null);

  const aiValueAvailable = useMemo(() => hasValueEngineData(races), [races]);

  const handlePick = useCallback((strategy) => {
    if (busyStrategy) return;
    if (!window.confirm(CONFIRM_MESSAGE)) return;

    setBusyStrategy(strategy);
    try {
      const result = runQuickPick({
        strategy,
        races,
        avgSpendPerRace,
        kMultiplier,
        perRaceFloor,
        totalStartingCapital,
      });

      if (result.aborted) {
        window.alert(result.message || 'Quick Pick could not build a valid stable. Please select runners manually.');
        return;
      }

      applyGeneratedStable(result.allocations);
    } catch (err) {
      console.error('[QuickPickBar] quick pick failed:', err);
      window.alert('Something went wrong generating your stable. Please try again or select manually.');
    } finally {
      setBusyStrategy(null);
    }
  }, [busyStrategy, races, avgSpendPerRace, kMultiplier, perRaceFloor, totalStartingCapital, applyGeneratedStable]);

  return (
    <div className="ss-quickpick-bar">
      <div className="ss-quickpick-label">
        <span className="ss-quickpick-title">Quick Pick</span>
        <span className="ss-quickpick-subtitle">Auto-fill your stable</span>
      </div>
      <div className="ss-quickpick-buttons">
        <button
          type="button"
          className="ss-quickpick-btn"
          onClick={() => handlePick(QUICK_PICK_STRATEGIES.RANDOM)}
          disabled={!!busyStrategy}
        >
          {busyStrategy === QUICK_PICK_STRATEGIES.RANDOM ? '⟳' : '🎲'} Random
        </button>
        <button
          type="button"
          className="ss-quickpick-btn"
          onClick={() => handlePick(QUICK_PICK_STRATEGIES.MARKET_FANCIES)}
          disabled={!!busyStrategy}
        >
          {busyStrategy === QUICK_PICK_STRATEGIES.MARKET_FANCIES ? '⟳' : '⭐'} Market Fancies
        </button>
        <button
          type="button"
          className="ss-quickpick-btn ss-quickpick-btn--ai"
          onClick={() => handlePick(QUICK_PICK_STRATEGIES.AI_VALUE)}
          disabled={!!busyStrategy}
        >
          {busyStrategy === QUICK_PICK_STRATEGIES.AI_VALUE
            ? '⟳ Working…'
            : aiValueAvailable ? '🤖 AI Value' : '💎 Budget Roughies'}
        </button>
      </div>
    </div>
  );
};

export default QuickPickBar;
